import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import CodeIcon from '@mui/icons-material/Code';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';

const CodePanelContainer = styled(Box)({
  position: 'fixed',
  top: '40px',
  right: 0,
  height: `calc(100vh - 40px)`,
  background: '#1a1a1a',
  borderLeft: '1px solid rgba(255, 255, 255, 0.1)',
  zIndex: 1000,
  transition: 'transform 0.3s ease',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.3)',
});

const CodePanelHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 16px',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  background: 'linear-gradient(135deg, #252526 0%, #1e1e1e 100%)',
  minHeight: '48px',
  backdropFilter: 'blur(10px)',
});

const CodeContent = styled(Box)({
  flex: 1,
  padding: '20px',
  overflow: 'auto',
  fontFamily: '"Fira Code", "Monaco", "Consolas", "Courier New", monospace',
  fontSize: '13px',
  lineHeight: '1.7',
  color: '#d4d4d4',
  background: '#1e1e1e',
  '&::-webkit-scrollbar': {
    width: '10px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#2d2d2d',
    borderRadius: '5px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#555',
    borderRadius: '5px',
    border: '2px solid #2d2d2d',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#777',
  },
  '& div': {
    marginBottom: '2px',
  },
});

const CodeLine = styled(Box)({
  marginBottom: '4px',
  padding: '3px 6px',
  borderRadius: '3px',
  transition: 'all 0.2s ease',
  '&:hover': {
    background: 'rgba(255, 255, 255, 0.08)',
    transform: 'translateX(2px)',
  },
});

const Keyword = styled('span')({
  color: '#569cd6',
  fontWeight: 'bold',
  textShadow: '0 0 1px rgba(86, 156, 214, 0.3)',
});

const String = styled('span')({
  color: '#ce9178',
  fontWeight: '500',
});

const Number = styled('span')({
  color: '#b5cea8',
  fontWeight: 'bold',
});

const Comment = styled('span')({
  color: '#6a9955',
  fontStyle: 'italic',
  fontWeight: '400',
});

const Property = styled('span')({
  color: '#9cdcfe',
  fontWeight: '500',
});

const Type = styled('span')({
  color: '#4ec9b0',
  fontWeight: 'bold',
});

const ColorIndicator = styled('span')({
  display: 'inline-block',
  width: '12px',
  height: '12px',
  borderRadius: '2px',
  marginRight: '8px',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  verticalAlign: 'middle',
});

const SectionHeader = styled('div')({
  marginTop: '20px',
  marginBottom: '15px',
  padding: '8px 12px',
  background: 'rgba(255, 255, 255, 0.05)',
  borderRadius: '6px',
  borderLeft: '4px solid #569cd6',
  fontWeight: 'bold',
  fontSize: '14px',
  color: '#ffffff',
});

const NodeHeader = styled('div')({
  marginTop: '15px',
  marginBottom: '10px',
  padding: '6px 10px',
  background: 'rgba(255, 255, 255, 0.03)',
  borderRadius: '4px',
  borderLeft: '3px solid',
  fontSize: '12px',
  fontWeight: '500',
});

const VerticalDragHandle = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '4px',
  height: '100%',
  cursor: 'col-resize',
  transition: 'background-color 0.2s ease',
  zIndex: 1001,
  '&:hover': {
    background: 'rgba(255, 255, 255, 0.15)',
  },
  '&:active': {
    background: 'rgba(255, 255, 255, 0.25)',
  },
});

const ToggleButton = styled(IconButton)({
  position: 'fixed',
  top: '48px',
  right: '12px',
  zIndex: 999,
  background: 'rgba(30, 30, 30, 0.95)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  color: '#ffffff',
  backdropFilter: 'blur(10px)',
  transition: 'all 0.2s ease',
  '&:hover': {
    background: 'rgba(45, 45, 45, 0.95)',
    transform: 'scale(1.05)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
  },
});

const formatGraphData = (graph) => {
  if (!graph || (!graph.concepts && !graph.individuals)) {
    return '<Comment>// No graph data available</Comment>';
  }

  let code = '';
  
  // Collect all relationships for summary
  let allRelationships = [];
  
  // Format concepts with better structure and color coding
  if (graph.concepts && graph.concepts.length > 0) {
    code += '<Comment>// CONCEPTS </Comment>\n';
    
    graph.concepts.forEach((concept, index) => {
      const color = concept.color || '#3b82f6';
      const colorHex = color.startsWith('#') ? color : '#3b82f6';
      const title = concept.title || concept.id || 'Untitled';
      const id = concept.id || `concept_${index}`;
      
      // Create color-coded concept header
      code += `<div style="margin: 15px 0 10px 0; padding: 8px 12px; background: rgba(255, 255, 255, 0.03); border-radius: 6px; border-left: 4px solid ${colorHex};">`;
      code += `<Comment>// Concept ${index + 1}: ${title}</Comment>\n`;
      code += `</div>`;
      
      code += `<Keyword>concept</Keyword> <Property>${id}</Property>: {\n`;
      
      // Basic properties with color indicator
      code += `  <Property>title</Property>: <String>"${title}"</String>,\n`;
      code += `  <Property>id</Property>: <String>"${id}"</String>,\n`;
      code += `  <Property>color</Property>: <String>"${colorHex}"</String>,\n`;
      if (concept.uid) {
        code += `  <Property>uid</Property>: <String>"${concept.uid}"</String>,\n`;
      }
      
      // AKO relationships (inheritance)
      if (concept.ako && concept.ako.length > 0) {
        code += `  <Property>ako</Property>: [\n`;
        concept.ako.forEach((parent, i) => {
          code += `    <String>"${parent}"</String>${i < concept.ako.length - 1 ? ',' : ''}\n`;
          allRelationships.push({
            from: id,
            to: parent,
            type: 'AKO',
            color: colorHex
          });
        });
        code += `  ],\n`;
      }
      
      // Predicates (properties/attributes)
      if (concept.predicates && Object.keys(concept.predicates).length > 0) {
        code += `  <Property>predicates</Property>: {\n`;
        Object.entries(concept.predicates).forEach(([key, value], i) => {
          const entries = Object.entries(concept.predicates);
          code += `    <Property>${key}</Property>: {\n`;
          code += `      <Property>type</Property>: <String>"${value.type || 'STRING'}"</String>`;
          if (value.range) {
            code += `,\n      <Property>range</Property>: <String>"${value.range}"</String>`;
          }
          if (value.description) {
            code += `,\n      <Property>description</Property>: <String>"${value.description}"</String>`;
          }
          code += `\n    }${i < entries.length - 1 ? ',' : ''}\n`;
        });
        code += `  }\n`;
      }
      
      code += `}\n\n`;
    });
  }
  
  // Format individuals with better structure and color coding
  if (graph.individuals && graph.individuals.length > 0) {
    code += '<Comment>// INDIVIDUALS </Comment>\n';
    
    graph.individuals.forEach((individ, index) => {
      const color = individ.color || '#3b82f6';
      const colorHex = color.startsWith('#') ? color : '#3b82f6';
      const title = individ.title || individ.id || 'Untitled';
      const id = individ.id || `individ_${index}`;
      
      // Create color-coded individual header
      code += `<div style="margin: 15px 0 10px 0; padding: 8px 12px; background: rgba(255, 255, 255, 0.03); border-radius: 6px; border-left: 4px solid ${colorHex};">`;
      code += `<Comment>// Individual ${index + 1}: ${title}</Comment>\n`;
      code += `</div>`;
      
      code += `<Keyword>individual</Keyword> <Property>${id}</Property>: {\n`;
      
      // Basic properties
      code += `  <Property>title</Property>: <String>"${title}"</String>,\n`;
      code += `  <Property>id</Property>: <String>"${id}"</String>,\n`;
      code += `  <Property>color</Property>: <String>"${colorHex}"</String>,\n`;
      if (individ.uid) {
        code += `  <Property>uid</Property>: <String>"${individ.uid}"</String>,\n`;
      }
      
      // ISA relationships (type membership)
      if (individ.isa && individ.isa.length > 0) {
        code += `  <Property>isa</Property>: [\n`;
        individ.isa.forEach((isa, i) => {
          code += `    <String>"${isa}"</String>${i < individ.isa.length - 1 ? ',' : ''}\n`;
          allRelationships.push({
            from: id,
            to: isa,
            type: 'ISA',
            color: colorHex
          });
        });
        code += `  ],\n`;
      }
      
      // Triplets (relationships and values)
      if (individ.triplets && Object.keys(individ.triplets).length > 0) {
        code += `  <Property>triplets</Property>: {\n`;
        Object.entries(individ.triplets).forEach(([key, value], i) => {
          const entries = Object.entries(individ.triplets);
          code += `    <Property>${key}</Property>: {\n`;
          code += `      <Property>type</Property>: <String>"${value.type || 'STRING'}"</String>`;
          if (value.value) {
            if (value.type === 'OBJECT') {
              code += `,\n      <Property>value</Property>: <String>"${value.value}"</String> <Comment>// Object reference</Comment>`;
              allRelationships.push({
                from: id,
                to: value.value,
                type: key,
                color: colorHex
              });
            } else {
              code += `,\n      <Property>value</Property>: <String>"${value.value}"</String>`;
            }
          }
          if (value.range) {
            code += `,\n      <Property>range</Property>: <String>"${value.range}"</String>`;
          }
          code += `\n    }${i < entries.length - 1 ? ',' : ''}\n`;
        });
        code += `  }\n`;
      }
      
      code += `}\n\n`;
    });
    // Footer with summary
    code += '<Comment>============================</Comment>\n';
  }
  
  // If no data, show a message
  if (!code) {
    code = '<Comment>// No graph data available</Comment>\n<Comment>// Graph is empty or loading...</Comment>';
  }
  
  return code;
};

const CodeGraphPanel = ({ isOpen, onToggle, width, onWidthChange, graphData, onRefresh, updateGraph }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdateTime, setLastUpdateTime] = useState(null);
  const startDragX = useRef(0);
  const startWidth = useRef(0);
  const containerRef = useRef(null);

  const handleDragStart = (e) => {
    e.preventDefault();
    startDragX.current = e.clientX;
    startWidth.current = width;
    setIsDragging(true);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    // Update timestamp immediately when refresh is triggered
    setLastUpdateTime(new Date());
    
    try {
      // Call updateGraph to trigger the meta_query request
      if (updateGraph) updateGraph();
      // Also call onRefresh to update the panel data
      if (onRefresh) {
        await onRefresh();
      }
    } finally {
      setTimeout(() => setIsRefreshing(false), 500);
    }
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging) {
        const delta = startDragX.current - e.clientX;
        const newWidth = startWidth.current + delta;
        const minWidth = 300;
        const maxWidth = window.innerWidth * 0.8;
        const constrainedWidth = Math.min(Math.max(newWidth, minWidth), maxWidth);
        onWidthChange(constrainedWidth);
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, onWidthChange]);

  const formattedCode = formatGraphData(graphData);

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return new Date().toLocaleTimeString();
    return timestamp.toLocaleTimeString();
  };

  return (
    <React.Fragment>
      <ToggleButton
        onClick={onToggle}
        size="small"
        sx={{
          right: '12px',
          transition: 'right 0.3s ease',
        }}
      >
        <CodeIcon />
      </ToggleButton>
      
      <CodePanelContainer
        ref={containerRef}
        sx={{
          width: `${width}px`,
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
        }}
      >
        <CodePanelHeader>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="h6" sx={{ color: '#ffffff', fontSize: '16px', fontWeight: 600 }}>
              JSON
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
              <Typography variant="caption" sx={{ color: '#888', fontSize: '11px' }}>
                {graphData.concepts ? graphData.concepts.length : 0} concepts, {graphData.individuals ? graphData.individuals.length : 0} individuals
              </Typography>
              <Typography variant="caption" sx={{ color: '#666', fontSize: '10px' }}>
                Last update: {formatTimestamp(lastUpdateTime)}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              onClick={handleRefresh}
              size="small"
              sx={{ 
                color: '#ffffff',
                transform: isRefreshing ? 'rotate(360deg)' : 'rotate(0deg)',
                transition: 'transform 0.5s ease',
              }}
              title="Refresh graph data"
              disabled={isRefreshing}
            >
              <RefreshIcon />
            </IconButton>
            <IconButton
              onClick={onToggle}
              size="small"
              sx={{ color: '#ffffff' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </CodePanelHeader>
        
        <CodeContent>
          {isRefreshing && (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1, 
              mb: 2, 
              color: '#6a9955',
              fontStyle: 'italic',
              fontSize: '12px'
            }}>
              <RefreshIcon sx={{ fontSize: '14px', animation: 'spin 1s linear infinite' }} />
              Refreshing graph data...
            </Box>
          )}
          <div 
            dangerouslySetInnerHTML={{ __html: formattedCode }} 
            style={{
              fontSize: '12px',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              fontVariantLigatures: 'none',
              letterSpacing: '0.3px'
            }}
          />
        </CodeContent>
        
        <VerticalDragHandle onMouseDown={handleDragStart} />
      </CodePanelContainer>
      
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </React.Fragment>
  );
};

export default CodeGraphPanel; 